// NOTE: NSAI-5622 — the three mutually exclusive views of the chat-history
// sidebar. Only one owns the list at a time, which is what stops the folders
// from pushing the chats off the bottom of the drawer: with folders in their
// own view there are no chats below them to push.
export enum ESidebarView {
  CHATS = "chats",
  ASSISTANT_CHATS = "assistant_chats",
  FOLDERS = "folders",
}

// NOTE: the display order of the switch, and the list a persisted value is
// validated against.
export const SIDEBAR_VIEWS: readonly ESidebarView[] = [
  ESidebarView.CHATS,
  ESidebarView.ASSISTANT_CHATS,
  ESidebarView.FOLDERS,
];

export const DEFAULT_SIDEBAR_VIEW = ESidebarView.CHATS;

// NOTE: the stored value is whatever was on the device last — an older build's
// spelling, or nothing at all — so it is checked rather than trusted.
export const isSidebarView = (value: unknown): value is ESidebarView =>
  typeof value === "string" && SIDEBAR_VIEWS.some((view) => view === value);

interface IGetSidebarViewsProps {
  hasAssistantReadPermission: boolean;
}

// NOTE: NSAI-6771 — the views a member may open, in display order. A member
// whose role reads no assistants has the Assistants item hidden from the
// navigation, so a tab of chats with assistants would contradict it; the tab
// is dropped rather than shown empty. Both clients render their tab row from
// this list, so web and mobile hide it under the same rule.
export const getSidebarViews = ({
  hasAssistantReadPermission,
}: IGetSidebarViewsProps): readonly ESidebarView[] =>
  hasAssistantReadPermission
    ? SIDEBAR_VIEWS
    : SIDEBAR_VIEWS.filter((view) => view !== ESidebarView.ASSISTANT_CHATS);

interface IResolveSidebarViewProps {
  view: ESidebarView;
  views: readonly ESidebarView[];
}

// NOTE: NSAI-6771 — the remembered view can name a tab the member may no
// longer open (their role lost assistants read since it was stored), which
// would leave a pane open with no tab selected. The default is shown instead.
// The stored value is left alone: permissions arrive after the first render,
// and overwriting it then would forget the choice of a member who does have
// access.
export const resolveSidebarView = ({
  view,
  views,
}: IResolveSidebarViewProps): ESidebarView =>
  views.includes(view) ? view : DEFAULT_SIDEBAR_VIEW;
